import type { AnnotationLabelT, GenericAnnotationReviewDataT, LabellingValidationsT, RelationshipStateT, TutorialStepT } from '../../../common/types/types';
import type { AnnotationUnderUserReviewMapT } from '../../../common/types/types';
import type { OutputRelationshipT, RelationshipLabelDefinitionT, RelationshipStoreWithLabelDataT } from '../../../modernization-common/relationships';
import type { TimeDisplayMode } from '../../types';
import type { BOUNDING_BOX_TOOLS, GRID_MODE, POLYGON_TOOLS, SAM_CLICK, SEGMENTATION_TOOLS, SHORTCUT_REGISTRATION_ERROR, SKELETON_TOOLS, TOOL, VIDEO_PLAYER_CONTROL_TYPES } from '../constants';
import type { PROPAGATION_METHOD } from '../reducers/actions/constants';
import type { AnnotationT } from './annotations.type';
import type { MediaItemT } from './studio-interface.type';
export type ToolT = (typeof TOOL)[keyof typeof TOOL];
export type SubToolT = (typeof BOUNDING_BOX_TOOLS)[keyof typeof BOUNDING_BOX_TOOLS] | (typeof SEGMENTATION_TOOLS)[keyof typeof SEGMENTATION_TOOLS] | (typeof POLYGON_TOOLS)[keyof typeof POLYGON_TOOLS] | (typeof SKELETON_TOOLS)[keyof typeof SKELETON_TOOLS];
export type ToolToSubToolMap = {
  RECTANGLE: (typeof BOUNDING_BOX_TOOLS)[keyof typeof BOUNDING_BOX_TOOLS];
  SEGMENTATION: (typeof SEGMENTATION_TOOLS)[keyof typeof SEGMENTATION_TOOLS];
  POLYGON: (typeof POLYGON_TOOLS)[keyof typeof POLYGON_TOOLS];
  SKELETON: (typeof SKELETON_TOOLS)[keyof typeof SKELETON_TOOLS];
};
export type ToolWithSubToolsT<T extends ToolT = ToolT> = {
  tool: T;
  subTool?: T extends keyof ToolToSubToolMap ? ToolToSubToolMap[T] : undefined;
};
export type GridModeT = (typeof GRID_MODE)[keyof typeof GRID_MODE];
export const EDITOR_MODE_TYPE = ({
  RELATIONSHIP: 'RELATIONSHIP',
  LABELING: 'LABELING'
} as const);
export type EditorModeT = (typeof EDITOR_MODE_TYPE)[keyof typeof EDITOR_MODE_TYPE];
export type MetaDataT = {
  height: number;
  width: number;
  rotation: number;
};
export type AnnotationStoreT = {
  [key: string]: AnnotationT;
};
export type AnnotationReviewStoreT = {
  [key: string]: GenericAnnotationReviewDataT;
};
export type FrameStateT = {
  imageUrl: string;
  annotationStore: AnnotationStoreT;
  annotationMLStore?: AnnotationStoreT;
  annotationConsensusStore?: AnnotationStoreT;
  relationshipStore?: RelationshipStoreWithLabelDataT;
};
export type AnnotationObjectMetaT = {
  strokeColor: string;
  annotationLabel: string;
};
export type BrushSettingsT = {
  selectedTool: (typeof SEGMENTATION_TOOLS)[keyof typeof SEGMENTATION_TOOLS] | (typeof POLYGON_TOOLS)[keyof typeof POLYGON_TOOLS] | (typeof SKELETON_TOOLS)[keyof typeof SKELETON_TOOLS];
  eraserSelected: boolean;
  size?: number;
  superpixelCount?: number;
  inclusionExclusion?: keyof typeof SAM_CLICK;
};
export type LabelStateT = {
  dataUrl: string | undefined | null;
  mediaFileReference: string | undefined | null;
  frames: FrameStateT[];
  currentFrame: number;
  hoveredFrame: number | null;
  selectedAnnotationLabel: AnnotationLabelT | undefined | null;
  selectedTool: ToolT;
  // TODO: https://t3.uberinternal.com/browse/TOPENG-33815
  selectedSubTool?: SubToolT | undefined;
  brushToolSettings: BrushSettingsT;
  selectedAnnotationIds: Array<string>;
  multiSelectEnabled: boolean;
  focusedAnnotationIdx: string | undefined | null;
  gridMode: GridModeT;
  snapEnabled: boolean;
  metaData: MetaDataT;
  validations: LabellingValidationsT;
  showAnnotationDetails: boolean;
  showMLAnnotations: boolean;
  annotationReviewStore: AnnotationReviewStoreT;
  annotationLabels: AnnotationLabelT[];
  superpixel: {
    superpixelSegmentsNumbers?: number[];
  };
  editorMode: EditorModeT;
  relationshipMode: RelationshipStateT | undefined;
  frameRate?: number | null | undefined;
  fileType: string;
  annotationUnderUserReview: AnnotationUnderUserReviewMapT;
  /* On accepting an annotation under review, we add it to annotationsToTrack to track it across specified frames.
  We use the annotationUnderReview ID as the unique identifier since annotationID varies per label in segmentation and differs from the ID used in f7-uLabel to store the data fetched from SAM. */
  annotationsToTrack?: Array<string>;
  isLoading: boolean;
  copiedAnnotationId: string | null;
  annotationPropagationMethod: (typeof PROPAGATION_METHOD)[keyof typeof PROPAGATION_METHOD];
  hideNoEntityCheckbox?: boolean;
  timeDisplayMode?: TimeDisplayMode;
  multiVideoList?: MediaItemT[];
  relationshipStore?: Record<string, OutputRelationshipT>;
  relationshipLabels?: RelationshipLabelDefinitionT[];
  sam3Config?: {
    isSAM3Enabled: boolean;
    isSAM3PolygonToolsEnabled: boolean;
    isSAM3PolygonTightnessSliderEnabled: boolean;
  };
  isVideoLabelingM4Enabled?: boolean;
  enableKeyframeStepperTracking?: boolean;
};
export type InitialLabelStateT = Partial<LabelStateT>;
export type VideoPlayerControlTypesT = (typeof VIDEO_PLAYER_CONTROL_TYPES)[keyof typeof VIDEO_PLAYER_CONTROL_TYPES];
export type ShortcutRegistrationErrorT = {
  type: typeof SHORTCUT_REGISTRATION_ERROR.TYPE;
  errorKey: string;
  message: typeof SHORTCUT_REGISTRATION_ERROR.MESSAGE;
};
export type TutorialProviderT = {
  addStep(stepData: TutorialStepT): Promise<boolean>;
  next(stepId: string): void;
  hideStep(stepId: string): void;
  showStep(stepId: string): void;
};
export type UpdatedSequenceRangeT = {
  id: string;
  newStart: number;
  newEnd: number;
};
export type StageElementT = {
  name: string;
  element: React.JSX.Element;
};
export type SegmentationPathT = {
  id: string;
  title: string;
  pathData: string;
};
export type ImageQuadrantValidationConfig = {
  isEnabled: boolean;
  minimumAnnotations: number;
  maxSingleQuadrantSharePercent: number;
  annotationLabelsToExclude: string[];
};